import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { adminService, Admin } from '../../../services/adminService';
import { profileService, Profile } from '../../../services/profileService';
import { useRights } from '../../../hooks/useRights';
import { useModal } from '../../../context/ModalContext';
import { getErrorTranslation } from '../../../utils/errorUtils';
import { ResourceType } from '../../../services/billService';
import { Pencil, Trash2, Lock, Receipt } from 'lucide-react';
import '../../../styles/index.css';
import './Admins.css';
import TableActions from '../../../components/TableActions';

const Admins: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRight } = useRights();
  const { showConfirm, showAlert } = useModal();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [editForm, setEditForm] = useState({
    userName: '',
    profileId: '',
    firstName: '',
    lastName: '',
    email: '',
    enabled: true,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    userName: '',
    profileId: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    enabled: true,
  });
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordModalAdmin, setPasswordModalAdmin] = useState<Admin | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    loadAdmins();
    loadProfiles();
  }, []);

  const adminProfiles = useMemo(() => profiles.filter(p => p.type === 'ADMIN'), [profiles]);

  useEffect(() => {
    if (isCreating && adminProfiles.length > 0 && !createForm.profileId) {
      setCreateForm(prev => ({ ...prev, profileId: adminProfiles[0].id }));
    }
  }, [isCreating, adminProfiles, createForm.profileId]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllAdmins();
      setAdmins(data);
      setError('');
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    } finally {
      setLoading(false);
    }
  };

  const loadProfiles = async () => {
    if (!hasRight('admins:c') && !hasRight('admins:u')) {
      return;
    }
    try {
      const data = await profileService.getAllProfiles();
      setProfiles(data);
    } catch (e: any) {
      console.error('Failed to load profiles:', e);
    }
  };

  const handleEdit = (admin: Admin) => {
    setEditingAdmin(admin);
    setEditForm({
      userName: admin.userName,
      profileId: admin.profileId || '',
      firstName: admin.firstName || '',
      lastName: admin.lastName || '',
      email: admin.email || '',
      enabled: admin.enabled !== undefined ? admin.enabled : true,
    });
  };

  const handleCancelEdit = () => {
    setEditingAdmin(null);
    setEditForm({ userName: '', profileId: '', firstName: '', lastName: '', email: '', enabled: true });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setCreateForm({ userName: '', profileId: '', password: '', firstName: '', lastName: '', email: '', enabled: true });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({ userName: '', profileId: '', password: '', firstName: '', lastName: '', email: '', enabled: true });
  };

  const handleOpenPasswordModal = (admin: Admin) => {
    setPasswordModalAdmin(admin);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordModalAdmin(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleSavePassword = async () => {
    if (!passwordModalAdmin) return;

    // Clear previous errors
    setPasswordError('');

    // Validate passwords
    if (!newPassword.trim()) {
      setPasswordError(t('admins.passwordRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('admins.passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t('admins.passwordTooShort'));
      return;
    }

    try {
      await adminService.updateAdmin(passwordModalAdmin.id, {
        userName: passwordModalAdmin.userName,
        profileId: passwordModalAdmin.profileId,
        password: newPassword,
        firstName: passwordModalAdmin.firstName,
        lastName: passwordModalAdmin.lastName,
        email: passwordModalAdmin.email,
        enabled: passwordModalAdmin.enabled,
      });
      await loadAdmins();
      handleClosePasswordModal();
    } catch (e: any) {
      setPasswordError(getErrorTranslation(e, t));
    }
  };

  const handleSaveCreate = async () => {
    if (!createForm.userName.trim()) {
      setError(t('admins.userNameRequired'));
      return;
    }

    if (!createForm.password || !createForm.password.trim()) {
      setError(t('admins.passwordRequired'));
      return;
    }
    if (!createForm.profileId) {
      setError(t('admins.profileRequired'));
      return;
    }

    try {
      await adminService.createAdmin({
        userName: createForm.userName,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        email: createForm.email,
        password: createForm.password,
        profileId: createForm.profileId,
        enabled: createForm.enabled,
      });
      await loadAdmins();
      handleCancelCreate();
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    }
  };

  const handleSave = async () => {
    if (!editingAdmin) return;
    if (!editForm.profileId) {
      setError(t('admins.profileRequired'));
      return;
    }

    try {
      await adminService.updateAdmin(editingAdmin.id, {
        userName: editForm.userName,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        profileId: editForm.profileId,
        enabled: editForm.enabled,
      });
      await loadAdmins();
      handleCancelEdit();
    } catch (e: any) {
      setError(getErrorTranslation(e, t));
    }
  };

  const handleDelete = (admin: Admin) => {
    showConfirm(
      t('common.delete'),
      t('admins.confirmDelete', { userName: admin.userName }),
      async () => {
        try {
          await adminService.deleteAdmin(admin.id);
          await loadAdmins();
          setError('');
        } catch (e: any) {
          setError(e.response?.data?.message || 'Failed to delete admin');
          showAlert(t('common.error'), e.response?.data?.message || 'Failed to delete admin');
          console.error('Delete admin error:', e);
        }
      }
    );
  };

  if (loading) {
    return <div className="bo-container">{t('common.loading')}</div>;
  }

  return (
    <div className="bo-container">
      <div className="bo-header">
        <h2>{t('admins.title')}</h2>
        {hasRight('admins:c') && (
          <button className="btn-create" onClick={handleCreate}>
            {t('admins.createAdmin')}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Create Admin Modal */}
      {isCreating && (
        <div className="modal-overlay" onClick={handleCancelCreate}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admins.createAdmin')}</h3>
              <button className="btn-close-modal" onClick={handleCancelCreate}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('admins.userName')} *</label>
                <input
                  type="text"
                  value={createForm.userName}
                  onChange={(e) => setCreateForm({ ...createForm, userName: e.target.value })}
                  placeholder={t('admins.userNamePlaceholder')}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admins.firstName')}</label>
                  <input
                    type="text"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    placeholder={t('admins.firstNamePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admins.lastName')}</label>
                  <input
                    type="text"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    placeholder={t('admins.lastNamePlaceholder')}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admins.email')} ({t('admins.optional')})</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder={t('admins.emailPlaceholder')}
                />
              </div>
              <div className="form-group">
                <label>{t('admins.password')} *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder={t('admins.passwordPlaceholder')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admins.profile')} *</label>
                <select
                  value={createForm.profileId}
                  onChange={(e) => setCreateForm({ ...createForm, profileId: e.target.value })}
                  required
                >
                  <option value="">{t('admins.noProfile')}</option>
                  {adminProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                  <input
                    type="checkbox"
                    checked={createForm.enabled}
                    onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })}
                  />
                  {t('admins.enabled')}
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={handleSaveCreate}>
                {t('common.save')}
              </button>
              <button className="btn-cancel" onClick={handleCancelCreate}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bo-table-container">
        <table className="bo-table">
          <thead>
            <tr>
              <th>{t('admins.userName')}</th>
              <th>{t('admins.firstName')}</th>
              <th>{t('admins.lastName')}</th>
              <th>{t('admins.email')}</th>
              <th>{t('admins.profile')}</th>
              <th>{t('admins.enabled')}</th>
              <th>{t('admins.created')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  {t('admins.noAdmins')}
                </td>
              </tr>
            ) : (
              admins.map((admin) => (
                <tr key={admin.id}>
                  <>
                    <td>{admin.userName}</td>
                    <td>{admin.firstName || '-'}</td>
                    <td>{admin.lastName || '-'}</td>
                    <td>{admin.email || '-'}</td>
                    <td>
                      {profiles.find((p) => p.id === admin.profileId)?.name || '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${admin.enabled ? 'enabled' : 'disabled'}`}>
                        {admin.enabled ? t('common.enabled') : t('common.disabled')}
                      </span>
                    </td>
                    <td>
                      {admin.creationDatetime
                        ? new Date(admin.creationDatetime).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      <TableActions actions={[
                        ...(hasRight('admins:u') ? [{
                          label: t('common.edit'),
                          onClick: () => handleEdit(admin),
                          className: 'btn-edit',
                          icon: <Pencil size={16} />,
                          hideLabelDesktop: true
                        }] : []),
                        ...(hasRight('admins:u') ? [{
                          label: t('admins.changePassword'),
                          onClick: () => handleOpenPasswordModal(admin),
                          className: 'btn-password',
                          icon: <Lock size={16} />,
                          hideLabelDesktop: true
                        }] : []),
                        ...(hasRight('admins:d') ? [{
                          label: t('common.delete'),
                          onClick: () => handleDelete(admin),
                          className: 'btn-delete',
                          icon: <Trash2 size={16} />,
                          hideLabelDesktop: true,
                          variant: 'danger' as const
                        }] : []),
                        ...(hasRight('bills:r') ? [{
                          label: t('admins.viewBills'),
                          onClick: () => navigate(`/bills?resourceType=${ResourceType.ADMIN}&resourceId=${admin.id}`),
                          className: 'btn-bills',
                          icon: <Receipt size={16} />,
                          hideLabelDesktop: true
                        }] : [])
                      ]} />
                    </td>
                  </>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Admin Modal */}
      {editingAdmin && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admins.editAdmin')}</h3>
              <button className="btn-close-modal" onClick={handleCancelEdit}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('admins.userName')} *</label>
                <input
                  type="text"
                  value={editForm.userName}
                  onChange={(e) => setEditForm({ ...editForm, userName: e.target.value })}
                  required
                  disabled
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admins.firstName')}</label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admins.lastName')}</label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admins.email')}</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('admins.profile')} *</label>
                <select
                  value={editForm.profileId}
                  onChange={(e) => setEditForm({ ...editForm, profileId: e.target.value })}
                  required
                >
                  <option value="">{t('admins.noProfile')}</option>
                  {adminProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                  <input
                    type="checkbox"
                    checked={editForm.enabled}
                    onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                  />
                  {t('admins.enabled')}
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={handleSave}>
                {t('common.save')}
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {passwordModalOpen && passwordModalAdmin && (
        <div className="modal-overlay" onClick={handleClosePasswordModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('admins.changePassword')} - {passwordModalAdmin.userName}</h3>
              <button className="btn-close-modal" onClick={handleClosePasswordModal}>×</button>
            </div>
            <div className="modal-body">
              {passwordError && <div className="error-message" style={{ marginBottom: '16px' }}>{passwordError}</div>}
              <div className="form-group">
                <label>{t('admins.newPassword')} *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder={t('admins.newPasswordPlaceholder')}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>{t('admins.confirmPassword')} *</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder={t('admins.confirmPasswordPlaceholder')}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={handleSavePassword}>
                {t('common.save')}
              </button>
              <button className="btn-cancel" onClick={handleClosePasswordModal}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;

